import { useGetUser } from 'hooks/useGetUser';
import React, { createContext, FC, useContext, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { ADDRESS_KIND_LOGIN } from 'utils/constants';
import { NewAccessesType, NewUserType } from 'types/new-user';
import { useUserAuth } from 'hooks/useUserAuth';

const screenPerMutations = {
  createOrderLedgerEntry: [
    'cash-to-close',
    'charges',
    'earnest-and-commissions',
    'disbursements-and-receipts',
    'accounting',
    'revenue'
  ],
  updateOrderPolicy: ['policy-info-and-rates'],
  updateOrderConfiguration: ['dates-and-config'],
  createOrderFile: ['documents'],
  createOrderLoan: ['loans'],
  createOrderNote: ['notes'],
  sendNotification: ['notifications'],
  createOrderAssociation: ['parties'],
  createOrderMortgage: ['payoffs'],
  updateOrderProjected: ['projected-payments'],
  createOrderProperty: ['properties'],
  createOrderProration: ['taxes-and-prorations'],
  createTransferRequest: ['wire-transfers'],
  updateOrderTokens: ['schedule-closing'],
  createParty: ['contacts'],
  createChargeSetting: ['charge-settings'],
  createAutoTemplate: ['document-templates'],
  createEmail: ['email-templates'],
  createRole: ['roles']
};

export type CurrentUser = {
  is_admin: boolean;
  is_agent: boolean;
  login_email: string;
  has_user: boolean;
  accesses: NewAccessesType[];
  allowedRoutes?: Set<string>
  settings: {
    language: 'en' | 'es';
    theme: 'light';
  };
} & NewUserType;

type UserInformationContextType = {
  token: string | null;
  currentUser: CurrentUser | null;
  setUserOnboarded: (value: boolean) => void;
  isCurrentUserLoading: boolean;
  userIsTitleAdmin: boolean;
};

const UserInformationContext = createContext<UserInformationContextType | null>(null);

export const UserInformationProvider: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const { isAuthenticated, token } = useUserAuth();

  const { isLoading: isCurrentUserLoading } = useGetUser({
    config: {
      enabled: isAuthenticated && token !== '',
      refetchOnWindowFocus: false,
      onSuccess: (userResponse) => {
        if (userResponse) {
          const user: CurrentUser = {
            ...userResponse.getUser.party,
            is_agent: userResponse.getUser.is_agent,
            login_email: userResponse.getUser.login_email,
            has_user: userResponse.getUser.has_user,
            is_admin: userResponse.getUser.is_admin,
            allowedRoutes: new Set(userResponse.getUser.party.permissions
              .filter(f => Object.keys(screenPerMutations)
                .some(g => g === f.element))
              .map(v => screenPerMutations[v.element]).flat()),
            accesses: [...userResponse.getUser.accesses],
            settings: {
              language: 'en',
              theme: 'light',
            },
          };

          const { username } = userResponse.getUser.party;
          if (username !== null && username !== undefined && username.length) {
            user.emails = [
              ...user.emails,
              {
                _id: uuidV4(),
                address: username,
                kind: ADDRESS_KIND_LOGIN,
              },
            ];
          }

          setCurrentUser(user);
        }
      },
      // No session clearing here: a confirmed auth failure is handled by the
      // GraphQL client (refresh + retry, then clearSession -> `unauthenticated`).
    },
  });

  const setUserOnboarded = (value: boolean) => {
    setCurrentUser((prevCurrentUser) => {
      return {
        ...prevCurrentUser,
        onboarded: value,
      } as CurrentUser;
    });
  };

  const userIsTitleAdmin = currentUser != null && currentUser.accesses.length > 0;

  return (
    <UserInformationContext.Provider
      value={{
        token,
        currentUser,
        setUserOnboarded,
        isCurrentUserLoading,
        userIsTitleAdmin,
      }}
    >
      {children}
    </UserInformationContext.Provider>
  );
};

export const useUserInformationContext = () => {
  const methods = useContext(UserInformationContext) as UserInformationContextType;
  return methods;
};
